// src/tracing/TracerProvider.jsx

import React, { useEffect } from "react";

// -------------------------
// OpenTelemetry imports
// -------------------------
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { SimpleSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { Resource } from "@opentelemetry/resources";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { W3CTraceContextPropagator } from "@opentelemetry/core";

// Metrics
import { MeterProvider, PeriodicExportingMetricReader } from "@opentelemetry/sdk-metrics";
import { OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";

// Logs
import { LoggerProvider, BatchLogRecordProcessor } from "@opentelemetry/sdk-logs";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { context, trace } from "@opentelemetry/api"; // fixed import

// -------------------------
// CHANGE THESE VALUES
// -------------------------
const SERVICE_NAME = "APEX_BANK_FRONTEND"; // Frontend service name
const OTLP_BASE_URL = "https://apexbanking.live/v1"; // OTLP collector URL
const HOST_NAME = "apex-ohio"; // Optional hostname

// -------------------------
// Tracing
// -------------------------
const traceExporter = new OTLPTraceExporter({ url: `${OTLP_BASE_URL}/traces` });
const tracerProvider = new WebTracerProvider({
  resource: new Resource({ "service.name": SERVICE_NAME, "host.name": HOST_NAME }),
});
tracerProvider.addSpanProcessor(new SimpleSpanProcessor(traceExporter));
tracerProvider.register({
  contextManager: new ZoneContextManager(),
  propagator: new W3CTraceContextPropagator(),
});
export const tracer = tracerProvider.getTracer(SERVICE_NAME);

// -------------------------
// Fetch Instrumentation
// -------------------------
const fetchInstrumentation = new FetchInstrumentation({
  propagateTraceHeaderCorsUrls: [/.*/],
  applyCustomAttributesOnSpan: (span, request, result) => {
    if (result) {
      span.setAttribute("http.status_code", result.status);
      if (!result.ok) {
        span.setStatus({ code: 2, message: `HTTP ${result.status}` });
        span.addEvent("error", {
          "error.message": `HTTP ${result.status}: ${result.statusText}`,
          "error.url": request.url,
          "error.method": request.method,
        });
      }
    }
  },
});
registerInstrumentations({ instrumentations: [fetchInstrumentation] });

// -------------------------
// Metrics
// -------------------------
const metricExporter = new OTLPMetricExporter({ url: `${OTLP_BASE_URL}/metrics` });
const meterProvider = new MeterProvider({
  metricReaders: [new PeriodicExportingMetricReader({ exporter: metricExporter })],
});
const meter = meterProvider.getMeter(SERVICE_NAME);

// Example: button clicks
export const clickCounter = meter.createCounter("button_clicks", { description: "Count of button clicks" });

// -------------------------
// Logs
// -------------------------
const logExporter = new OTLPLogExporter({ url: `${OTLP_BASE_URL}/logs` });
const loggerProvider = new LoggerProvider({
  logRecordProcessors: [new BatchLogRecordProcessor(logExporter)],
});
export const logger = loggerProvider.getLogger(SERVICE_NAME);

// Utility log function with trace/span correlation
export function logEvent(message, severity = "INFO") {
  const activeSpan = trace.getSpan(context.active()); // fixed method
  const traceId = activeSpan?.spanContext()?.traceId;
  const spanId = activeSpan?.spanContext()?.spanId;

  logger.emit({
    body: message,
    severityText: severity, // string: "INFO", "WARN", "ERROR"
    attributes: { trace_id: traceId, span_id: spanId },
  });
}

// Optional initial log
logEvent("Frontend TraceProvider initialized");

// -------------------------
// React TraceProvider Component
// -------------------------
const TraceProvider = ({ children }) => {
  useEffect(() => {
    // You can add any startup logic here if needed
  }, []);

  return <>{children}</>;
};

export default TraceProvider;
